import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button } from "@nexus/ui";

import type {
  EntityTiming,
  OrderStatusTimeOrder,
  StatusTimeSegment,
} from "../api/order-status-time.api";
import { formatStatusDuration } from "../lib/format-duration";
import {
  averageOrderTiming,
  groupOrdersByPartner,
  type ClientBreakdown,
  type PartnerGroup,
} from "../lib/group-client-partner";

const SEGMENT_COLOR: Record<string, string> = {
  SCRIPT: "var(--muted-foreground)",
  SENT_TO_PRODUCTION: "var(--chart-blue)",
  SUBMITTED_FOR_FEEDBACK: "var(--chart-teal)",
  SENT_FOR_REVISION: "var(--chart-orange)",
  SUBMITTED_FOR_FINAL_APPROVAL: "var(--chart-purple)",
  ORDER_DELIVERED: "var(--chart-green)",
  COMPLETED_PRE_ADGE: "var(--chart-green)",
  CANCELLED: "var(--muted-foreground)",
};

function statusLabel(t: TFunction, segment: StatusTimeSegment): string {
  return t(`orderStatusTime.status.${segment.status}`, segment.label);
}

function segmentPhrase(t: TFunction, segment: StatusTimeSegment): string {
  const label = statusLabel(t, segment);
  const duration = formatStatusDuration(segment.durationMs, t);
  if (segment.isOpen) {
    return t("orderStatusTime.segmentSoFar", {
      defaultValue: "{{label}} · {{duration}} so far",
      label,
      duration,
    });
  }
  return t("orderStatusTime.segment", {
    defaultValue: "{{label}} · {{duration}}",
    label,
    duration,
  });
}

function totalPhrase(t: TFunction, timing: EntityTiming): string {
  const duration = formatStatusDuration(timing.totalMs, t);
  if (timing.isOpen) {
    return t("orderStatusTime.totalSoFar", {
      defaultValue: "{{duration}} so far",
      duration,
    });
  }
  return duration;
}

function StatusTimeBar({ timing }: { timing: EntityTiming }) {
  const { t } = useTranslation();
  if (!timing.breakdownAvailable) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("orderStatusTime.breakdownUnavailable", "Step breakdown unavailable")}
      </p>
    );
  }

  const total = timing.segments.reduce((sum, segment) => sum + segment.durationMs, 0);
  const widthBasis = Math.max(total, timing.totalMs, 1);
  const label = timing.segments.map((segment) => segmentPhrase(t, segment)).join(". ");

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <div
        className="flex h-3 overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label={label}
      >
        {timing.segments.map((segment) => (
          <div
            key={segment.status}
            style={{
              width: `${(segment.durationMs / widthBasis) * 100}%`,
              backgroundColor: SEGMENT_COLOR[segment.status] ?? "var(--chart-blue)",
            }}
          />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
        {timing.segments.map((segment) => (
          <li key={segment.status}>{segmentPhrase(t, segment)}</li>
        ))}
      </ul>
    </div>
  );
}

function TimingRow({
  timing,
  title,
}: {
  timing: EntityTiming;
  title?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
      {title ? <p className="w-40 shrink-0 text-sm font-medium">{title}</p> : null}
      <StatusTimeBar timing={timing} />
      <p className="shrink-0 text-sm font-medium tabular-nums">{totalPhrase(t, timing)}</p>
    </div>
  );
}

function formatOrderDate(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(language, {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function countLabel(
  t: TFunction,
  key: string,
  count: number,
  one: string,
  other: string,
): string {
  return t(key, {
    defaultValue_one: one,
    defaultValue_other: other,
    count,
  });
}

function ClientOrders({ client }: { client: ClientBreakdown }) {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const scriptCount = client.orders.reduce((sum, order) => sum + order.scripts.length, 0);
  const scriptsLabel = countLabel(
    t,
    "orderStatusTime.scripts",
    scriptCount,
    "{{count}} script",
    "{{count}} scripts",
  );
  const ordersLabel = countLabel(
    t,
    "orderStatusTime.orders",
    client.orders.length,
    "{{count}} order",
    "{{count}} orders",
  );

  return (
    <li className="flex flex-col gap-3 rounded-md border border-border p-3">
      <h4 className="text-sm font-medium">
        <Button
          type="button"
          variant="ghost"
          className="h-auto w-full justify-between px-2 text-sm font-medium"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          <span>{client.clientName}</span>
          <span className="font-normal text-muted-foreground">
            {t("orderStatusTime.groupSummary", {
              defaultValue: "{{scripts}} · {{orders}}",
              scripts: scriptsLabel,
              orders: ordersLabel,
            })}
          </span>
        </Button>
      </h4>
      <TimingRow timing={client.timing} />
      {isOpen ? (
        <ul className="flex flex-col gap-4 border-t border-border pt-3">
          {client.orders.map((order) => (
            <li key={order.id} className="flex flex-col gap-3">
              <p className="text-sm font-medium">
                {t("orderStatusTime.orderPlaced", {
                  defaultValue: "Order {{date}}",
                  date: formatOrderDate(order.createdAt, i18n.language),
                })}
              </p>
              {order.scripts.length > 0 ? (
                <ul className="flex flex-col gap-3">
                  {order.scripts.map((script, index) => (
                    <li key={script.id}>
                      <TimingRow
                        timing={script.timing}
                        title={
                          script.title ??
                          t("orderStatusTime.scriptFallback", {
                            defaultValue: "Script {{position}}",
                            position: index + 1,
                          })
                        }
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <TimingRow timing={order.timing} />
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function PartnerCard({ partner }: { partner: PartnerGroup }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const scriptCount = partner.clients.reduce(
    (sum, client) => sum + client.orders.reduce((orders, order) => orders + order.scripts.length, 0),
    0,
  );
  const clientsLabel = countLabel(
    t,
    "orderStatusTime.clients",
    partner.clients.length,
    "{{count}} client",
    "{{count}} clients",
  );
  const scriptsLabel = countLabel(
    t,
    "orderStatusTime.scripts",
    scriptCount,
    "{{count}} script",
    "{{count}} scripts",
  );

  return (
    <article className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <h3 className="text-base font-medium">
        <Button
          type="button"
          variant="ghost"
          className="h-auto w-full justify-between px-2 text-base font-medium"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          <span>{partner.partnerName}</span>
          <span className="text-sm font-normal text-muted-foreground">
            {t("orderStatusTime.partnerSummary", {
              defaultValue: "{{clients}} · {{scripts}}",
              clients: clientsLabel,
              scripts: scriptsLabel,
            })}
          </span>
        </Button>
      </h3>
      <TimingRow timing={partner.timing} />
      {isOpen ? (
        <ul className="flex flex-col gap-3 border-t border-border pt-3">
          {partner.clients.map((client) => (
            <ClientOrders key={client.key} client={client} />
          ))}
        </ul>
      ) : null}
    </article>
  );
}

function SystemTiming({ orders }: { orders: OrderStatusTimeOrder[] }) {
  const { t } = useTranslation();
  const ordersLabel = countLabel(
    t,
    "orderStatusTime.orders",
    orders.length,
    "{{count}} order",
    "{{count}} orders",
  );

  return (
    <article className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-medium">{t("orderStatusTime.systemTitle", "All orders")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("orderStatusTime.systemSummary", {
            defaultValue: "Average of {{orders}}",
            orders: ordersLabel,
          })}
        </p>
      </div>
      <TimingRow timing={averageOrderTiming(orders)} />
    </article>
  );
}

export function OrderStatusTimeView({ orders }: { orders: OrderStatusTimeOrder[] }) {
  const { t } = useTranslation();
  const partners = groupOrdersByPartner(orders);

  if (orders.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("orderStatusTime.empty", "No orders yet")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SystemTiming orders={orders} />
      {partners.map((partner) => (
        <PartnerCard key={partner.key} partner={partner} />
      ))}
    </div>
  );
}
